<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { MarGlobalSearchDialog, type MarGlobalSearchResult } from '@nimling/components-nimtech'
import { getLogSearch, getSearch } from '@/lib/api'
import { openLogs } from '@/lib/view'
import { stepFor } from '@/stores/range'
import type { LogRecord, SearchHit } from '@/types'

const emit = defineEmits<{ close: [] }>()

const LOG_SPAN_MS = 24 * 60 * 60 * 1000

const router = useRouter()
const query = ref('')
const hits = ref<SearchHit[]>([])
const logs = ref<LogRecord[]>([])
const loading = ref(false)

const kinds = { view: { group: 'Views', icon: 'eye' }, page: { group: 'Pages', icon: 'dashboard' } } as const

const items = computed<MarGlobalSearchResult[]>(() => [
  ...(['view', 'page'] as const).flatMap((kind) =>
    hits.value
      .filter((hit) => hit.kind === kind)
      .map((hit) => ({ id: `${kind}:${hit.id}`, title: hit.name, group: kinds[kind].group, icon: kinds[kind].icon, description: hit.description })),
  ),
  ...logs.value.map((entry) => ({
    id: `log:${entry.id}`,
    title: entry.service || 'Log',
    group: 'Logs',
    icon: 'list',
    description: entry.body,
    tags: entry.severity_text ? [entry.severity_text] : undefined,
    path: new Date(entry.time).toLocaleString(),
  })),
])

let timer: ReturnType<typeof setTimeout> | undefined
let token = 0

const runSearch = async (value: string) => {
  const current = ++token
  loading.value = true
  const to = Date.now()
  const from = to - LOG_SPAN_MS
  const range = { from: new Date(from).toISOString(), to: new Date(to).toISOString(), step: stepFor(from, to) }
  const [found, lines] = await Promise.allSettled([getSearch(value, 12), value.length < 2 ? Promise.resolve(null) : getLogSearch(value, range, undefined, 8)])
  if (current !== token) return
  hits.value = found.status === 'fulfilled' ? found.value : []
  logs.value = lines.status === 'fulfilled' && lines.value ? lines.value.entries : []
  loading.value = false
}

watch(query, (value) => {
  clearTimeout(timer)
  timer = setTimeout(() => void runSearch(value.trim()), 300)
})

void runSearch('')

const onClose = (item?: MarGlobalSearchResult) => {
  emit('close')
  if (!item) return
  const [kind, id] = item.id.split(':')
  if (kind === 'view') void router.push(`/views/${id}`)
  else if (kind === 'page') void router.push(`/pages/${id}`)
  const entry = kind === 'log' ? logs.value.find((row) => row.id === id) : undefined
  if (!entry) return
  if (entry.source_id) void router.push({ path: `/streams/${entry.source_id}`, query: { at: entry.time, line: entry.id } })
  else void openLogs({ service: entry.service, at: entry.time })
}

onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <MarGlobalSearchDialog
    :query="query"
    :results="items"
    :loading="loading"
    placeholder="Search views, pages and logs"
    empty="Nothing matches"
    hint="Type to search views, pages and the last day of logs"
    @update:query="query = $event"
    @close="onClose"
  />
</template>
